import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconStatusChange } from '@tabler/icons-react';
import { BetaSchemaForm, ProFormColumnsType } from '@ant-design/pro-components';
import { CrudMode, FormItemValueType } from '@ohpen/enso-ui';

import { IntermediaryResponse, ComplianceStatus } from '@pmp/api';
import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';

export const StatusTab: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;

  const columns: ProFormColumnsType<IntermediaryResponse, 'text'>[] = useMemo(
    () => [
      {
        title: t('lender.brokers.labels.compliance'),
        dataIndex: ['compliance', 'complianceStatus'],
        valueType: FormItemValueType.radio,
        readonly,
        fieldProps: {
          options: [
            { label: t('lender.firms.labels.pending'), value: ComplianceStatus.UNDECIDED },
            { label: t('lender.firms.labels.notRequired'), value: ComplianceStatus.NOT_REQUIRED },
            { label: t('lender.firms.labels.failed'), value: ComplianceStatus.FAILED },
            { label: t('lender.firms.labels.blocked'), value: ComplianceStatus.BLOCKED },
            { label: t('lender.firms.labels.passed'), value: ComplianceStatus.PASSED },
          ],
        },
        colProps: { span: 24 },
      },
      {
        valueType: 'group',
        gutter: [24, 0],
        columns: [
          {
            title: t('lender.brokers.labels.complianceReReviewRequired'),
            dataIndex: ['compliance', 'reviewRequired'],
            valueType: 'radio',
            readonly,
            fieldProps: {
              options: [
                { label: t('global.labels.yes'), value: true },
                { label: t('global.labels.no'), value: false },
              ],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.watchlist'),
            dataIndex: ['compliance', 'onTheWatchlist'],
            valueType: 'radio',
            readonly,
            fieldProps: {
              options: [
                { label: t('global.labels.yes'), value: true },
                { label: t('global.labels.no'), value: false },
              ],
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        title: t('lender.brokers.labels.complianceRecord'),
        valueType: 'group',
        colProps: { span: 24 },
        gutter: [24, 0],
        columns: [
          {
            title: t('lender.firms.labels.addedToPanel'),
            dataIndex: 'addedToPanel',
            valueType: 'date',
            readonly: true,
            colProps: { span: 6 },
          },
          {
            title: t('global.labels.addedBy'),
            dataIndex: 'addedBy',
            valueType: 'text',
            readonly: true,
            colProps: { span: 6 },
          },
          {
            title: t('global.labels.lastUpdate'),
            dataIndex: 'lastUpdate',
            valueType: 'date',
            readonly: true,
            colProps: { span: 6 },
          },
          {
            title: t('global.labels.updatedBy'),
            dataIndex: 'updatedBy',
            valueType: 'text',
            readonly: true,
            colProps: { span: 6 },
          },
        ],
      },
    ],
    [readonly, t],
  );

  return (
    <CrudFormCard icon={<IconStatusChange />} title={t('lender.firms.labels.status')}>
      <BetaSchemaForm<IntermediaryResponse> form={form} layoutType="Embed" columns={columns} submitter={false} />
    </CrudFormCard>
  );
};
